import { useEffect, useState } from 'react';
import { SelectItem } from '@/components/ui/select';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { useFonts } from '@/context/FontProvider';
import type { FormProps } from '@builder4/shared/components/Form';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_NODE_NAME } from '@/utils/constants';

const NodeFieldLabelFontWeight = ({ className = 'w-full' }: { className?: string }) => {
  const { fontManager } = useFonts();
  const { id, actions, props: nodeProps, node } = useInspectorNode<Partial<FormProps>>();

  const { getBPProp, setBPProp } = useBreakpointAwareProp(node?.data.displayName || FORM_NODE_NAME);

  // Read props using breakpoint-aware getter
  const fieldLabelFontFamily = getBPProp(nodeProps, 'fieldLabelFontFamily');
  const fieldLabelFontWeight = getBPProp(nodeProps, 'fieldLabelFontWeight');

  const [weights, setWeights] = useState<
    Array<{ label: string; value: string; weight: string; style: string }>
  >([]);
  const [currentWeight, setCurrentWeight] = useState(String(fieldLabelFontWeight || 500));

  useEffect(() => {
    setCurrentWeight(String(fieldLabelFontWeight || 500));
  }, [fieldLabelFontWeight]);

  useEffect(() => {
    if (fieldLabelFontFamily && fontManager) {
      const availableWeights = fontManager.getAvailableWeights(fieldLabelFontFamily);
      const nonItalicWeights = availableWeights.filter((w) => !/italic/i.test(w.value));
      setWeights(nonItalicWeights);
    }
  }, [fieldLabelFontFamily, fontManager]);

  const handleWeightChange = (variant: string) => {
    const weight = weights.find((w) => w.value === variant);
    if (!weight) return;

    actions.setProp(id, (props: FormProps) => {
      setBPProp(props, 'fieldLabelFontWeight', weight.weight, FORM_NODE_NAME);
    });
  };

  if (weights.length === 0) {
    return (
      <SelectField className={className} size="small" text="Weight" defaultValue="500">
        <SelectItem value="500">Loading...</SelectItem>
      </SelectField>
    );
  }

  return (
    <SelectField
      onValueChange={handleWeightChange}
      className={className}
      defaultValue={currentWeight}
      size="small"
      text="Weight"
      truncate={false}
    >
      {weights.map((weight) => (
        <SelectItem key={weight.value} value={weight.weight}>
          {weight.label}
        </SelectItem>
      ))}
    </SelectField>
  );
};

export default NodeFieldLabelFontWeight;
